/**
 * Event Builder — client validation and pre-flight gates (plan 3.4 rules 1 & 10, 8.2).
 *
 * This is the single place where "may this section be sent?" is decided. The UI derives its
 * disabled states / badges from the same helpers so payload and UI can never disagree.
 *
 * Every 403 the server can return (`feature_locked`, `plan_limit_exceeded`,
 * `payment_integration_required`, `setup_incomplete`) is fatal to the builder because
 * `axios-instance.js` redirects on them — so each of those conditions is checked here first.
 *
 * Issue shape: { step, field, message, section, anchor }
 *   step    — which save endpoint the field belongs to (STEPS.*)
 *   section — which builder section shows it (SECTIONS.*)
 *   anchor  — DOM id of the block/control to scroll to (footer "n required items left")
 */

import { FEATURE_IDS } from "../../../../constants/featureAccess";
import { canUseOrganizationStaff, hasPooledAvailability } from "../../../../utils/organizationStaffAccess";
import { isAtPlanLimit } from "../../../../utils/planLimits";
import {
  ASSIGNMENT_MODE,
  CONFERENCING_VENUES,
  D,
  DURATION_MIN,
  E,
  FLAT_PERCENT,
  FORMAT,
  GROUP_MIN_CAPACITY,
  INVITEE_CALL_STATUS,
  KIND,
  PAYMENT_TYPE,
  SCHEDULE_TYPE,
  SECTIONS,
  STAFF_METHOD,
  STEPS,
  VALIDITY,
  VENUE,
  VENUE_APP_ID,
} from "./constants";
import { featureEnabled } from "./payloads";

export { featureEnabled };

// ---- helpers -----------------------------------------------------------------------------------------

const blank = (v) => v === null || v === undefined || String(v).trim() === "";

/** Correct HH:mm → minutes (today's utility concatenates strings; the intent — end after start — is kept). */
export const timeToMinutes = (hhmm) => {
  if (blank(hhmm)) return NaN;
  const [h, m] = String(hhmm).split(":");
  const hours = parseInt(h, 10);
  const minutes = parseInt(m, 10) || 0;
  if (Number.isNaN(hours)) return NaN;
  return hours * 60 + minutes;
};

export const isVenueConnected = (venueId, connectedVenues) => {
  const appId = VENUE_APP_ID[parseInt(venueId, 10)];
  if (!appId) return true; // not a conferencing venue
  return (connectedVenues || []).some(
    (v) => parseInt(v.app_id, 10) === appId && (v.connection_status === undefined || v.connection_status === E)
  );
};

export const hasPaymentProvider = (connectedVenues) =>
  (connectedVenues || []).some(
    (v) => [10, 11].includes(parseInt(v.app_id, 10)) && (v.connection_status === undefined || v.connection_status === E)
  );

const issue = (step, section, field, message, anchor) => ({ step, section, field, message, anchor });

// ---- Type -------------------------------------------------------------------------------------------------

export function validateType(type, meta, lookups, features) {
  const issues = [];
  const S = SECTIONS.QUICK;

  if (blank(type.event_name)) {
    issues.push(issue(STEPS.TYPE, S, "event_name", "Event name is required.", "event-name-setting"));
  }
  if (blank(type.event_url)) {
    issues.push(issue(STEPS.TYPE, SECTIONS.INVITEE, "event_url", "Event URL is required.", "event-url-setting"));
  }

  const format = String(type.event_format_id);
  if (format === FORMAT.GROUP) {
    if (!featureEnabled(features, FEATURE_IDS.GROUP_EVENTS)) {
      // server: requireFeature(5) → 403 feature_locked → redirect. Never send.
      issues.push(
        issue(STEPS.TYPE, S, "event_format_id", "Group events are not in your plan. Switch to One-to-one or upgrade.", "booking-format-setting")
      );
    }
    if ((parseInt(type.no_of_attendies, 10) || 0) < GROUP_MIN_CAPACITY) {
      issues.push(issue(STEPS.TYPE, S, "no_of_attendies", "Number of attendees should be greater than 1.", "booking-format-setting"));
    }
  }

  const venueId = parseInt(type.venue_id, 10);
  if (!venueId) {
    issues.push(issue(STEPS.TYPE, S, "venue_id", "Choose a meeting location.", "location-setting"));
  } else {
    if (CONFERENCING_VENUES.includes(venueId) && lookups.connectedVenuesLoaded && !isVenueConnected(venueId, lookups.connectedVenues)) {
      // D21: Meet, Zoom and Teams all require a connection (a booking without one cannot produce a link)
      issues.push(issue(STEPS.TYPE, S, "venue_id", "Connect this conferencing app before using it.", "location-setting"));
    }
    if (venueId === VENUE.IN_PERSON) {
      if (blank(type.venue_location)) issues.push(issue(STEPS.TYPE, S, "venue_location", "Venue location is required.", "location-setting"));
      if (blank(type.venue_location_status)) issues.push(issue(STEPS.TYPE, S, "venue_location_status", "Venue location status is required.", "location-setting"));
    }
    if (venueId === VENUE.PHONE) {
      if (blank(type.invitee_call_status)) issues.push(issue(STEPS.TYPE, S, "invitee_call_status", "Invitee call status is required.", "location-setting"));
      if (type.invitee_call_status === INVITEE_CALL_STATUS.INVITEE && blank(type.host_phone_no)) {
        issues.push(issue(STEPS.TYPE, S, "host_phone_no", "Host phone number is required.", "location-setting"));
      }
    }
    if (venueId === VENUE.OTHER) {
      if (blank(type.venue_other_notes)) issues.push(issue(STEPS.TYPE, S, "venue_other_notes", "Notes are required.", "location-setting"));
      if (blank(type.venue_other_display_status)) issues.push(issue(STEPS.TYPE, S, "venue_other_display_status", "Choose when to show the notes.", "location-setting"));
    }
  }

  // Paid kind needs the Collect Payments feature AND a connected gateway before `is_event_paid: 'E'`
  // is ever sent (server: requireFeature(9) + assertCanCreatePaidEvent → 403s).
  if (meta.kind === KIND.PAID) {
    if (!featureEnabled(features, FEATURE_IDS.COLLECT_PAYMENTS)) {
      issues.push(issue(STEPS.TYPE, S, "kind", "Paid events are not in your plan.", "payment-setting"));
    } else if (lookups.paymentConnected === false) {
      issues.push(issue(STEPS.TYPE, S, "kind", "Connect Stripe or PayPal before creating a paid event.", "payment-setting"));
    }
  }

  return issues;
}

// ---- Details ------------------------------------------------------------------------------------------------

/** Same semantics as add-new-event-details.jsx `validateLineItems`. */
export const validateLineItems = (items = [], label = "Pricing") => {
  for (let i = 0; i < (items || []).length; i++) {
    const item = items[i] || {};
    const hasName = String(item.item_name || "").trim() !== "";
    const hasPrice = item.price !== "" && item.price !== null && item.price !== undefined;
    if (hasName && !hasPrice) return `${label} row ${i + 1}: please enter a price for "${item.item_name}".`;
    if (!hasName && hasPrice) return `${label} row ${i + 1}: please enter an item name.`;
  }
  return null;
};

export function validateDetails(details, meta, features) {
  const issues = [];

  if (blank(details.event_color)) {
    issues.push(issue(STEPS.DETAILS, SECTIONS.QUICK, "event_color", "Event color is required.", "event-color-setting"));
  }
  if (details.max_no_booking_status === E && (parseInt(details.max_no_of_booking, 10) || 0) === 0) {
    issues.push(issue(STEPS.DETAILS, SECTIONS.AVAILABILITY, "max_no_of_booking", "Max number of bookings needs to be greater than 0.", "max-bookings-setting"));
  }

  if (meta.kind !== KIND.PAID) return issues;

  // Quick setup block 7 — price is required for a paid event (pricing[0].price)
  const first = (details.pricing || [])[0] || {};
  if (blank(first.price) || Number(first.price) <= 0) {
    issues.push(issue(STEPS.DETAILS, SECTIONS.QUICK, "price", "Enter the event price.", "payment-setting"));
  }
  const pricingError = validateLineItems(details.pricing, "Pricing");
  if (pricingError) issues.push(issue(STEPS.DETAILS, SECTIONS.PAYMENTS, "pricing", pricingError, "paid-items-setting"));
  const addOnError = validateLineItems(details.add_ons, "Add-on");
  if (addOnError) issues.push(issue(STEPS.DETAILS, SECTIONS.PAYMENTS, "add_ons", addOnError, "paid-addons-setting"));

  if (details.event_prepaid_status === E) {
    if (details.payments_type === PAYMENT_TYPE.PARTIAL && blank(details.payments_price)) {
      issues.push(issue(STEPS.DETAILS, SECTIONS.PAYMENTS, "payments_price", "Enter the first payment amount.", "payment-timing-setting"));
    }
    if (details.deposit_type === PAYMENT_TYPE.DEPOSIT) {
      const dep = Number(details.deposit_price);
      if (blank(details.deposit_price) || Number.isNaN(dep) || dep <= 0) {
        issues.push(issue(STEPS.DETAILS, SECTIONS.QUICK, "deposit_price", "Enter the deposit amount.", "payment-setting"));
      } else if (details.deposit_flat_per_type === "percent" && dep >= 100) {
        issues.push(issue(STEPS.DETAILS, SECTIONS.QUICK, "deposit_price", "Deposit must be less than 100%.", "payment-setting"));
      }
    }
  }

  if (details.staff_commission_status === E) {
    const rate = Number(details.staff_commission_rate) || 0;
    const flatCommission = details.staff_commission_type === FLAT_PERCENT.FLAT;
    if (rate <= 0) {
      issues.push(
        issue(
          STEPS.DETAILS,
          SECTIONS.PAYMENTS,
          "staff_commission_rate",
          flatCommission ? "Enter a commission amount greater than 0." : "Commission rate needs to be greater than 0.",
          "commission-setting"
        )
      );
    } else if (!flatCommission && rate >= 100) {
      issues.push(issue(STEPS.DETAILS, SECTIONS.PAYMENTS, "staff_commission_rate", "Commission rate needs to be less than 100.", "commission-setting"));
    }
  }

  if (details.refund_type === "partial") {
    const pct = Number(details.refund_percentage);
    if (blank(details.refund_percentage) || Number.isNaN(pct) || pct < 0 || pct > 100) {
      issues.push(issue(STEPS.DETAILS, SECTIONS.PAYMENTS, "refund_percentage", "Enter a refund percentage between 0 and 100.", "refund-setting"));
    }
  }

  return issues;
}

// ---- Availability ---------------------------------------------------------------------------------------------

export function validateIntervals(rows) {
  // rows: [{dayId|dated, schedules:[{start_time,end_time}]}] → { "dayId:idx" | "dated:idx": message }
  const errors = {};
  (rows || []).forEach((row) => {
    const key = row.dated ? `dated:${row.dated}` : `day:${row.dayId}`;
    (row.schedules || []).forEach((slot, idx) => {
      const start = timeToMinutes(slot.start_time);
      const end = timeToMinutes(slot.end_time);
      if (Number.isNaN(start) || Number.isNaN(end)) {
        errors[`${key}:${idx}`] = "Enter both a start and an end time.";
      } else if (start > end) {
        errors[`${key}:${idx}`] = "End time can't be earlier than the start time.";
      } else if (start === end) {
        errors[`${key}:${idx}`] = "Start time and end time can't be the same.";
      }
    });
  });
  return errors;
}

/** Ported from the old page's `assertRoundRobinAllowed` (lines 341–359). */
export const roundRobinAllowed = (method, { hadRoundRobin, limits }) => {
  if (method !== STAFF_METHOD.ROUND_ROBIN) return true;
  if (hadRoundRobin) return true;
  const data = limits && limits.round_robin;
  if (!data) return true;
  return !isAtPlanLimit(data.limit, data.used);
};

export function validateAvailability(availability, meta, lookups, { userPlan, features, limits } = {}) {
  const issues = [];
  const S = SECTIONS.AVAILABILITY;

  if (availability.invitee_set_duration_status !== E) {
    const d = parseInt(availability.event_duration_in_minutes, 10) || 0;
    if (d < DURATION_MIN) {
      issues.push(issue(STEPS.AVAILABILITY, SECTIONS.QUICK, "event_duration_in_minutes", "Choose a duration.", "duration-setting"));
    }
  }

  const eventRow = (availability.schedules || []).find((s) => s.type === SCHEDULE_TYPE.EVENT);
  const rows = eventRow ? eventRow.schedules : [];
  if (!rows.length || rows.every((r) => !(r.schedules || []).length)) {
    issues.push(issue(STEPS.AVAILABILITY, SECTIONS.QUICK, "schedules", "Add at least one available time.", "availability-setting"));
  }
  // the server converts every interval to UTC with the schedule's timezone (AvailabilityTrait);
  // an empty timezone would silently store wrong hours, so it is required once rows exist
  if (rows.length && blank(availability.timezone_id)) {
    issues.push(issue(STEPS.AVAILABILITY, S, "timezone_id", "Choose a time zone.", "schedule-toolbar"));
  }
  const intervalErrors = validateIntervals(rows);
  Object.keys(intervalErrors).forEach((k) => {
    issues.push(issue(STEPS.AVAILABILITY, S, `interval:${k}`, intervalErrors[k], "weekly-list"));
  });
  (availability.schedules || [])
    .filter((s) => s.type === SCHEDULE_TYPE.EVENT_STAFF)
    .forEach((staffRow) => {
      const errs = validateIntervals(staffRow.schedules);
      Object.keys(errs).forEach((k) => {
        issues.push(issue(STEPS.AVAILABILITY, S, `staff:${staffRow.staffId}:interval:${k}`, errs[k], "staff-hours"));
      });
    });

  if (availability.event_validity === VALIDITY.VALID_OVER && (parseInt(availability.valid_over_period, 10) || 0) <= 0) {
    issues.push(issue(STEPS.AVAILABILITY, S, "valid_over_period", "Valid over period (days) is required.", "validity-setting"));
  }
  if (availability.event_validity === VALIDITY.DATE_RANGE) {
    if (blank(availability.event_start_date) || blank(availability.event_end_date)) {
      issues.push(issue(STEPS.AVAILABILITY, S, "event_dates", "Select a start and an end date.", "validity-setting"));
    } else if (availability.event_start_date > availability.event_end_date) {
      issues.push(issue(STEPS.AVAILABILITY, S, "event_end_date", "Event end date must be on or after the start date.", "validity-setting"));
    }
  }

  // Hosts
  const mode = availability.assignmentMode || ASSIGNMENT_MODE.MY;
  if (mode !== ASSIGNMENT_MODE.MY) {
    if (!canUseOrganizationStaff(userPlan)) {
      // payload is normalised to type 1 anyway (rule 11) — nothing to block
    } else {
      if (!(availability.staffs || []).length) {
        issues.push(issue(STEPS.AVAILABILITY, SECTIONS.HOSTS, "staffs", "Add at least one staff member.", "assign-staff-setting"));
      }
      if (mode === ASSIGNMENT_MODE.AUTO && hasPooledAvailability(features)) {
        if (!availability.staff_assignment_method) {
          issues.push(issue(STEPS.AVAILABILITY, SECTIONS.HOSTS, "staff_assignment_method", "Choose Round-robin or Event rotation.", "assignment-setting"));
        } else if (!roundRobinAllowed(availability.staff_assignment_method, { hadRoundRobin: lookups.hadRoundRobin, limits })) {
          // server: assertCanAddRoundRobinEvent → 403 plan_limit_exceeded → redirect. Never send.
          issues.push(
            issue(STEPS.AVAILABILITY, SECTIONS.HOSTS, "staff_assignment_method", "You have reached the maximum number of round robin events allowed on your current plan.", "assignment-setting")
          );
        }
      }
    }
  }

  return issues;
}

// ---- Advanced ------------------------------------------------------------------------------------------------------

export function validateAdvanced(advanced, meta, lookups, { features, limits } = {}) {
  const issues = [];
  const S = SECTIONS.INVITEE;

  if (advanced.confirmation_page === "external") {
    if (!featureEnabled(features, FEATURE_IDS.REDIRECT_CUSTOM_PAGES)) {
      // payload forces 'default' (rule 11); nothing to block
    } else if (blank(advanced.confirmation_page_url)) {
      issues.push(issue(STEPS.ADVANCED, S, "confirmation_page_url", "Enter the redirect URL.", "confirmation-setting"));
    }
  }

  // staff confirmation cap: server 403 plan_limit_exceeded → redirect. Block when newly enabled at the cap.
  if (
    advanced.staff_can_confirm_booking === E &&
    featureEnabled(features, FEATURE_IDS.STAFF_CONFIRMATION) &&
    lookups.orgStaffCanConfirm === E &&
    !lookups.hadStaffConfirm
  ) {
    const data = limits && limits.staff_confirm;
    if (data && isAtPlanLimit(data.limit, data.used)) {
      issues.push(issue(STEPS.ADVANCED, S, "staff_can_confirm_booking", "Plan limit reached for staff confirmation events.", "confirmation-setting"));
    }
  }

  (advanced.questions || []).forEach((q, i) => {
    if (blank(q.question)) {
      issues.push(issue(STEPS.ADVANCED, S, `question:${i}`, "Enter the question text.", "questions-setting"));
    }
    if (["select", "radio", "checkbox"].includes(q.question_type)) {
      const opts = (q.details || []).filter((d) => !blank(d.field_label));
      if (!opts.length) issues.push(issue(STEPS.ADVANCED, S, `question:${i}:options`, "Add at least one option.", "questions-setting"));
    }
  });

  return issues;
}

// ---- All ------------------------------------------------------------------------------------------------------------

export function validateAll(state, redux) {
  const { type, details, availability, advanced, meta, lookups } = state;
  const { userPlan, features, limits } = redux || {};
  return {
    [STEPS.TYPE]: validateType(type, meta, lookups, features),
    [STEPS.DETAILS]: validateDetails(details, meta, features),
    [STEPS.AVAILABILITY]: validateAvailability(availability, meta, lookups, { userPlan, features, limits }),
    [STEPS.ADVANCED]: validateAdvanced(advanced, meta, lookups, { features, limits }),
  };
}

export const flattenIssues = (byStep) => Object.values(byStep || {}).reduce((acc, list) => acc.concat(list || []), []);

/** Fields (T) that the Quick setup name/URL blocks show inline from server 422s. */
export const SERVER_FIELD_MESSAGES = {
  event_name: "This name is already used.",
  event_url: "This URL is already used.",
};

/** Map a server 422 body to { step, fieldErrors, message }. */
export function mapServerError(step, errorBody) {
  const body = errorBody || {};
  const fieldErrors = {};
  const message = body.message || "Couldn't save.";

  // validation message bag: { data: { field: [msg] } } or { errors: {...} }
  const bag = body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : body.errors;
  if (bag && typeof bag === "object") {
    Object.keys(bag).forEach((k) => {
      const v = bag[k];
      fieldErrors[k] = Array.isArray(v) ? v[0] : String(v);
    });
  }

  // uniqueness errors come back as a bare message (trans api.error_event_url_not_available / name)
  if (step === STEPS.TYPE) {
    const lower = String(message).toLowerCase();
    if (lower.includes("url")) fieldErrors.event_url = SERVER_FIELD_MESSAGES.event_url;
    else if (lower.includes("name")) fieldErrors.event_name = SERVER_FIELD_MESSAGES.event_name;
  }

  return { step, fieldErrors, message };
}

export const gateLabels = {
  upgrade: "Upgrade",
  teams: "Teams plan",
  afterTrial: "After trial",
  comingSoon: "Coming soon",
};

export const isGroupFormat = (type) => String(type.event_format_id) === FORMAT.GROUP;
export const kindIsPaid = (meta) => meta.kind === KIND.PAID;
export const flagOn = (v) => v === E;
export const flagOff = (v) => v !== E;
export const notD = (v) => v !== D;
